import { Injectable, EventEmitter, OnDestroy, EnvironmentInjector, runInInjectionContext, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, firstValueFrom, of, zip } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { ReportService } from './report.service';
import { MenuService } from './menu.service';
import { BudgetSpendService } from './budgetSpend.service';
import { AcquisitionService } from './acquisition.service';
import { ReportManagementService } from './reportManagement.service';
import { FeatureFlagService } from './feature-flag.service';
import { PosthogService } from './posthog.service';
import { FavoritesService } from './favorites.service';
import {
    AllUserPermissions,
    IncidentReportPermissionIds,
    hasIncidentAccess,
    isIncidentSubmitterOnly
} from '@server/types/permissions';
import { Roles } from '@server/types/roles';
import { User } from '@server/types/user';
import { APP_CONSTANTS } from '../../app.constants';
import { LocalStorageService } from './local-storage.service';
import { LibraryService } from './library.service';
export { Roles, AllUserPermissions,  IncidentReportPermissionIds, User};



@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    // Shared API URL prefix sourced from APP_CONSTANTS so the "/api" mount
    // point is not hard-coded throughout this service.
    private readonly API_PREFIX = APP_CONSTANTS.API_PREFIX;

    apiToken = '';
    redirectUrl = '';
    sessionTimer;

    // Tracks an in-flight logout so that concurrent callers (e.g. the
    // inactivity monitor firing handleTimeout() while the token interceptor
    // simultaneously reacts to a 401 on some pending request) don't each
    // POST /api/login/logout. When two logout requests race, one of them
    // ends up canceled by the router navigation / state teardown performed
    // by the other, surfacing in the console as
    // "Http failure response for /api/login/logout: 0 Unknown Error".
    private logoutInFlight: Promise<boolean> | null = null;
    private manualLogout = false;

    private readonly onSessionStorage = (event: StorageEvent): void => {
        if (event.storageArea !== localStorage || event.key !== 'authToken') return;
        // Ignore delayed events for a session that has already changed again.
        if (event.newValue !== localStorage.getItem('authToken')) return;
        if (!event.newValue) {
            void this.logout();
        } else if (event.newValue !== this.apiToken) {
            // Restore the shared identity and permissions through the normal guard.
            window.location.reload();
        }
    };

    user: User = null;
    private user$ = new BehaviorSubject<User | null>(null);

    // Captured at construction so HTTP calls fired from teardown hooks
    // (e.g. a component's ngOnDestroy calling userLog) run their functional
    // interceptors — which use inject() — against the root injector rather
    // than whatever component context happens to be closing. Without this
    // the interceptor's inject() calls throw NG0205 mid-teardown.
    private readonly rootInjector = inject(EnvironmentInjector);
    private readonly dialog = inject(MatDialog);

    onUserLogin = new EventEmitter();

    constructor(
        private menuService: MenuService,
        private toastrService: ToastrService,
        private reportService: ReportService,
        private router: Router,
        private http: HttpClient,
        private budgetSpendService: BudgetSpendService,
        private acqService: AcquisitionService,
        private reportManagementService: ReportManagementService,
        private transloco: TranslocoService,
        private featureFlags: FeatureFlagService,
        private favorites: FavoritesService,
        private posthogService: PosthogService,
        private localStorageService: LocalStorageService,
        private libraryService: LibraryService
    ) {
        // Eagerly restore the API token from localStorage so that HTTP calls
        // made by APP_INITIALIZERs (e.g. feature-flags) already carry a valid
        // x-session-token header. Without this, those early requests send an
        // empty token, receive a 401, and the interceptor's error handler
        // removes the stored token — causing the subsequent reConnect() to
        // find nothing in localStorage and redirect to login on every refresh.
        const savedToken = localStorage.getItem('authToken');
        if (savedToken) {
            this.apiToken = savedToken;
        }

        window.addEventListener('storage', this.onSessionStorage);

        this.reportService.monitorChange
            .pipe(
                distinctUntilChanged(
                    (prev, curr) =>
                        // only continue if the branch or the library actually changed
                        prev.currentBranch === curr.currentBranch && prev.currentLibrary === curr.currentLibrary
                )
            )
            .subscribe((item) => {
                this.setLibraryMenuVisibility(item.currentLibrary, item.currentBranch);
            });
    }

    ngOnDestroy(): void {
        // during dev, the app can do hot reloads which do not cancel
        // intervals. We need to clear this to prevent multiple sessions
        // with different, expired contexts.
        clearInterval(this.sessionTimer);
        window.removeEventListener('storage', this.onSessionStorage);
    }

    getUser(): Observable<User | null> {
        return this.user$.asObservable();
    }

    redirectToHome(): void {
        this.router.navigateByUrl('/');
    }

    async checkSession(): Promise<boolean> {
        if (!this.apiToken || this.apiToken !== localStorage.getItem('authToken')) {
            this.logout();
            return false;
        }

        try {
            await firstValueFrom(
                this.http.get(`${this.API_PREFIX}/login/validSession`, {
                    params: {
                        userid: this.user.id.toString(),
                        token: this.apiToken
                    }
                })
            );

            this.monitorSession();
            return true;
        } catch (_e) {
            this.logout();
            return false;
        }
    }

    monitorSession(): void {
        if (!this.sessionTimer) {
            const intervalDuration = 5 * 60 * 1000; // 5 minutes
            this.sessionTimer = setInterval(() => this.checkSession(), intervalDuration);
        }
    }

    async reConnect(): Promise<boolean> {
        const savedToken = localStorage.getItem('authToken');
        if (!savedToken) {
            return false;
        }

        if (this.user && this.apiToken === savedToken) {
            this.monitorSession();
            return true;
        }

        // set the token so the interceptor will pick it up
        this.apiToken = savedToken;

        // try to login the user
        // LIQ-1583: make sure to try/catch otherwise app will hang, as reConnect() will fail
        let user = null;
        try {
            user = await firstValueFrom(
                this.http.get<{ user: User }>(`${this.API_PREFIX}/login/user`).pipe(map((res) => res.user))
            );
        } catch (_) {
            return false;
        }

        if (localStorage.getItem('authToken') !== savedToken) return false;
        this.user = user;

        if (!this.user) return false;
        // set the token so the interceptor will pick it up
        this.apiToken = this.user.token;
        this.user$.next(this.user);
        if (this.user.userName === undefined || this.apiToken.length < 1) return false;

        sessionStorage.setItem('sessionUser', JSON.stringify(this.user));
        // we do not want to keep doing this. this is continuing the circular dependency issue
        // by having report service and auth service push data to each other.
        // we also don't want [0] so there is a filter, but should be refactored to empty array.
        // TODO: LIQ-1303 to refactor this permission
        this.reportService.validBranches = this.user.permissions.branchLibrary.filter(Boolean);

        // Start fetching libraries early so the request runs in parallel
        // with checkSession() rather than sequentially after the guard.
        this.reportService.preloadLibraries();

        this.transloco.setActiveLang(this.user.language || 'en');
        this.userLog('Reconnected from Local Storage');

        const valid = await this.checkSession();
        if (!valid) return false;

        // Track session restoration in PostHog
        this.posthogService.trackSessionRestored(
            this.user,
            this.reportService.getLibraryName(),
            this.reportService.getBranchName()
        );

        this.favorites.loadUserFavorites(this.user.id);

        this.menuService.refreshMenu();
        this.setUserMenuVisibility();
        return true;
    }

    // Re-fetches the current user from the server and swaps it into the
    // cached session without a full page reload. `user.permissions` is
    // otherwise only refreshed on reConnect() (hard refresh) or a fresh
    // login, so an admin who grants themselves a new permission (e.g.
    // MetricsAdmin) via the user-management modal would otherwise see no
    // effect until they log out/in. Call this right after such a self-edit
    // save succeeds. Emits `true` on success and `false` on failure —
    // rather than swallowing the error — so the caller can decide how to
    // surface a refresh that didn't take (e.g. keep its dialog open).
    refreshCurrentUser(): Observable<boolean> {
        if (!this.user) return of(false);
        return this.http.get<{ user: User }>(`${this.API_PREFIX}/login/user`).pipe(
            map((res) => res.user),
            map((user) => {
                if (!user) return false;
                this.user = user;
                this.user$.next(this.user);
                sessionStorage.setItem('sessionUser', JSON.stringify(this.user));
                this.reportService.validBranches = this.user.permissions.branchLibrary.filter(Boolean);
                this.menuService.refreshMenu();
                this.setUserMenuVisibility();
                return true;
            }),
            catchError(() => of(false))
        );
    }

    // set visibility based on logged in user
    setUserMenuVisibility(): void {
        // Check if user has any incident role permission
        const hasAnyIncidentPermission = hasIncidentAccess(this.user);

        // Check if user is Submitter-only (no higher incident permissions)
        const isSubmitterOnly = isIncidentSubmitterOnly(this.user);

        // TODO: eventually, this will be managed by module access. for now, use this permission.
        if (this.user.permissions.onlyIncidentReports) {
            if (isSubmitterOnly) {
                // Submitter-only users see only Reports and PIM People (no Dashboard or Setup)
                this.menuService.setSubmitterOnlyMenu();
            } else {
                this.menuService.setOnlyIncidentReporting();
            }
        }

        // Book Lookup is admin-only (PlatformStaff / roleid 0)
        this.menuService.setMenuVisibility('bookLookup', this.user.roleid === Roles.PlatformStaff);
        // Buy Now uses the same PlatformStaff gate as Book Lookup
        this.menuService.setMenuVisibility('buyNow', this.user.roleid === Roles.PlatformStaff);

        // Show MetisDirector based on accessMetis permission
        this.menuService.setMenuVisibility('metisDirector', this.user.permissions.accessMetis);

        const hasMetricsPermission =
            this.user.permissions.metricsEnter ||
            this.user.permissions.metricsAdmin ||
            this.user.permissions.metricsCMTEnter ||
            this.user.permissions.metricsIQEnter ||
            this.user.permissions.metricsLock ||
            // metricsUpload grants the Daily Metrics file import, which lives on
            // this page — without it here the permission is unreachable.
            this.user.permissions.metricsUpload;
        this.menuService.setMenuVisibility('enterMetrics', hasMetricsPermission);

        this.menuService.setMenuVisibility('inventory', !!this.user.permissions.inventoryModule);

        if (hasAnyIncidentPermission) {
            this.menuService.setMenuVisibility('incidentReporting', true);
            // Setup/Admin menu requires admin permission (matches IncidentAdminGuard)
            const showAdmin = this.user.permissions.incidentReportAdmin || this.user.permissions.incidentReportManageUser;
            this.menuService.setMenuVisibility('incidentSetupAdmin', showAdmin);
            // Dashboard and People are hidden for Submitter-only users
            if (isSubmitterOnly) {
                this.menuService.setMenuVisibility('incidentReportingDashboard', false);
                this.menuService.setMenuVisibility('incidentPimPeople', false);
            }
        } else {
            this.menuService.setMenuVisibility('incidentReporting', false);
            this.menuService.setMenuVisibility('incidentPromo', true); // show promo page
        }

        // IQ Notify - show if user has any notify permission or is platform staff
        const isPlatformStaff = this.user.roleid === Roles.PlatformStaff;
        const hasAnyNotifyPermission =
            isPlatformStaff ||
            this.user.permissions.notifyManagerAdmin ||
            this.user.permissions.notifyManagerEditor ||
            this.user.permissions.notifyManagerViewer;

        if (hasAnyNotifyPermission) {
            this.menuService.setMenuVisibility('notify', true);
            this.menuService.setMenuVisibility(
                'notifySettings',
                isPlatformStaff || this.user.permissions.notifyManagerAdmin
            );
        } else {
            this.menuService.setMenuVisibility('notify', false);
            this.menuService.setMenuVisibility('notifySettings', false);
        }


        if (!this.user.permissions.patronAndCommunity) {
            this.menuService.setMenuVisibility('patronAndCommunity', false);
        }

        this.featureFlags.hasFeature('pageFiltersForm').subscribe((enabled) => {
            this.menuService.setMenuVisibility('pageFiltersTest', enabled);
        });

        this.featureFlags.hasFeature('programs').subscribe((enabled) => {
            this.menuService.setMenuVisibility('programs', enabled);
        });

        this.featureFlags.hasFeature('circ-tracker').subscribe((enabled) => {
            this.menuService.setMenuVisibility('circTracker', enabled);
        });

        this.featureFlags.hasFeature('scheduler').subscribe((enabled) => {
            // Setup & Admin is the Scheduler module's only page today and every
            // control on it writes, so it is shown only to Scheduler Admins —
            // which for now means the Schedules menu as a whole follows the
            // permission too, since hiding its one child would otherwise leave
            // an empty parent. When the Create Schedule and Schedule pages land
            // the parent goes back to the feature flag alone and only this
            // child keeps the permission check (matches incidentSetupAdmin).
            const isSchedulerAdmin = !!this.user.permissions.schedulerAdmin;
            this.menuService.setMenuVisibility('schedules', enabled && isSchedulerAdmin);
            this.menuService.setMenuVisibility('schedulerSetupAdmin', isSchedulerAdmin);
        });

        this.featureFlags.hasFeature('fundingIntelligence').subscribe((enabled) => {
            // Testing phase: the whole group is Platform Staff only, on top of the
            // feature flag (matches AdminGuard on the route). When it opens to
            // libraries, show the group on `enabled` alone; Catalog Admin keeps the
            // API's rule: Platform Staff by role, or the fundingAdmin permission.
            const isStaff = this.user.roleid === Roles.PlatformStaff;
            const canManageCatalog = isStaff || !!this.user.permissions.fundingAdmin;
            this.menuService.setMenuVisibility('fundingIntelligence', enabled && isStaff);
            this.menuService.setMenuVisibility('fundingCatalogAdmin', canManageCatalog);
        });
    }

    // set visibility based on the current library and branch
    setLibraryMenuVisibility(library: number, branch: string): void {
        if (!library || !branch) return;
        // Guard against unauthenticated contexts (e.g. the public dashboard route).
        // The public dashboard pushes to reportService.monitorChange to seed library
        // context for embedded widgets, which fires this subscription even though
        // there is no logged-in user. Without this check, accessing this.user.id
        // throws "Cannot read properties of null (reading 'id')" and breaks the
        // public dashboard render.
        if (!this.user) return;

        this.localStorageService.setSelectedLibrary(this.user.id, library);
        this.localStorageService.setSelectedBranch(this.user.id, branch);

        // Update PostHog properties when library/branch changes
        if (this.user && this.posthogService.isInitialized()) {
            try {
                this.posthogService.setPersonProperties({
                    libraryName: this.reportService.getLibraryName(),
                    branchName: this.reportService.getBranchName()
                });
            } catch (error) {
                console.error('Failed to update PostHog person properties on library/branch change:', error);
            }
        }

        // The FindIt tab uses the same check.
        this.menuService.setMenuVisibility('physicalCollectionByFindIt', this.libraryService.hasFindItData(library));

        // Check LCC records before showing LLC menu; the LCC tab uses the same check.
        this.menuService.setMenuVisibility('physicalCollectionByLLC', false);
        this.libraryService.hasLccRecords(library)
            .pipe(catchError(() => of(false)))
            .subscribe(hasLcc => this.menuService.setMenuVisibility('physicalCollectionByLLC', hasLcc));

        if (this.reportService.hasAnyBranchWithVenueID()) {
            this.menuService.setMenuVisibility('footTraffic', true);
        } else {
            this.menuService.setMenuVisibility('footTraffic', false);
        }

        if (
            this.reportService.branches.length < 2 &&
            !this.reportService.currentConsortium &&
            !this.reportService.currentAccountGroup
        ) {
            this.menuService.setMenuVisibility('physicalCircDetailByLocation', false);
            this.menuService.setMenuVisibility('physicalCollectionByBranch', false);
            this.menuService.setMenuVisibility('balanceReport', false);
            this.menuService.setMenuVisibility('diversityByBranch', false);
            if (
                location.href.indexOf('locationCirculation') !== -1 ||
                location.href.indexOf('collectionBranch') !== -1
            ) {
                this.router.navigateByUrl('/pages/landingPage');
            }
        } else {
            this.menuService.setMenuVisibility('physicalCircDetailByLocation', true);
            this.menuService.setMenuVisibility('physicalCollectionByBranch', true);
            this.menuService.setMenuVisibility('balanceReport', true);
            this.menuService.setMenuVisibility('diversityByBranch', true);
        }

        if (this.reportService.inConsortiumID) {
            this.menuService.setMenuVisibility('physicalCircDetailByLocation', true);
        }

        this.menuService.setMenuVisibility('collectionAcquire', false);
        this.menuService.setMenuVisibility('budgetSpending', false);
        this.menuService.setMenuVisibility('titlesOrdered', false);
        this.menuService.setMenuVisibility('openOrders', false);
        this.menuService.setMenuVisibility('invoices', false);

        zip([
            this.budgetSpendService.getBudgetSpend(),
            this.acqService.getTitlesOrdered(
                dayjs().subtract(3, 'months').format('YYYY-MM-DD'),
                dayjs().format('YYYY-MM-DD')
            )
        ]).subscribe(([budgetRes, polarisRes]) => {
            const budget = !!budgetRes.data.length;
            const polaris = !!polarisRes.data.length;

            if (budget || polaris) {
                this.menuService.setMenuVisibility('collectionAcquire', true);
            }

            if (budget) {
                this.menuService.setMenuVisibility('budgetSpending', true);
            }

            if (polaris) {
                this.menuService.setMenuVisibility('titlesOrdered', true);
                this.menuService.setMenuVisibility('openOrders', true);
                this.menuService.setMenuVisibility('invoices', true);
            }
        });

        this.reportManagementService.hasReports().subscribe((result) => {
            this.menuService.setMenuVisibility('customReports', result.hasReports);
        });
    }

    public async logout(manual = false): Promise<boolean> {
        // Re-entry guard: if a logout is already in progress, return the
        // same promise so callers don't fire duplicate /api/login/logout
        // POSTs. Without this, the inactivity monitor + interceptor 401
        // path can each independently invoke logout(), producing HTTP 0
        // "Unknown Error" when one of the parallel requests is aborted by
        // the navigation / state teardown performed by the other.
        if (this.logoutInFlight) {
            this.manualLogout = this.manualLogout || manual;
            return this.logoutInFlight;
        }

        this.manualLogout = manual;
        const claimLogout = (): boolean => {
            try {
                const ownsSession = !!this.apiToken && localStorage.getItem('authToken') === this.apiToken;
                localStorage.setItem('authLoggedOut', 'true');
                if (ownsSession) localStorage.removeItem('authToken');
                return ownsSession;
            } catch (error) {
                console.warn('Session storage unavailable during logout:', error);
                return !!this.apiToken;
            }
        };
        // Serialize the claim across tabs; storage events alone cannot make a
        // read/remove pair atomic when two timers expire simultaneously.
        const claim = navigator.locks
            ? navigator.locks.request('libraryiq-session-logout', claimLogout)
            : Promise.resolve(claimLogout());
        this.logoutInFlight = claim.then(ownsSession => this.performLogout(ownsSession)).finally(() => {
            this.logoutInFlight = null;
        });
        return this.logoutInFlight;
    }

    private async performLogout(ownsSession: boolean): Promise<boolean> {
        clearInterval(this.sessionTimer);
        this.sessionTimer = null;

        // Close every open modal before clearing auth state and navigating.
        // This guarantees session-expiration/logout paths never leave stale
        // overlays visible on top of the login screen.
        this.dialog.closeAll();

        let logoutUrl = '';

        // Track logout event before clearing user data
        if (this.user && ownsSession) {
            this.posthogService.trackUserLogout(this.user.id, this.user.accountId);
            try {
                const response = await firstValueFrom(
                    this.http.post<{ success: boolean; logoutUrl?: string }>(`${this.API_PREFIX}/login/logout`, {})
                );
                logoutUrl = response.logoutUrl || '';
            } catch (error) {
                // Swallow logout HTTP errors (including status 0 from
                // aborted requests during navigation). Client-side cleanup
                // below must still run so the user ends up on the login
                // page regardless of the server-side outcome.
                if (!(error instanceof HttpErrorResponse) || ![0, 401, 419].includes(error.status)) {
                    console.error('Logout request failed:', error);
                }
            }
        }

        // Reset PostHog session
        try {
            this.posthogService.reset();
        } catch (error) {
            console.error('PostHog error during session reset:', error);
        }

        // clearing session and redirecting first, so that any listeners are not getting
        // triggered by the library branch monitor changes while being logged out
        try {
            sessionStorage.removeItem('sessionUser');
        } catch (error) {
            console.warn('Session storage unavailable during logout cleanup:', error);
        }
        try {
            localStorage.removeItem('authLastActivity');
        } catch (error) {
            console.warn('Local storage unavailable during logout cleanup:', error);
        }

        this.favorites.resetUserFavorites();
        this.menuService.refreshMenu();

        this.apiToken = '';
        this.user = null;
        this.user$.next(null);
        this.reportService.currentLibrary = null;
        this.reportService.lastLibrary = null;
        this.reportService.validBranches = [];
        this.reportService.monitorChange.next({ currentLibrary: null, currentBranch: null });
        this.featureFlags.clearFeatureFlags();

        // Only the tab explicitly logging out resumes the default login flow.
        // This survives the provider logout redirect without restarting SSO in peers.
        if (ownsSession && this.manualLogout) {
            try {
                sessionStorage.setItem('oidcManualLogout', 'true');
            } catch {
                // The logged-out screen remains usable if storage is unavailable.
            }
        }

        if (logoutUrl) {
            window.location.assign(logoutUrl);
            return true;
        }

        await this.router.navigateByUrl('/pages/login');
        this.toastrService.info('pages.login.toasts.logoutSuccess');
        this.userLog('Logged Out');
        return true;
    }

    loginUser(user: User): void {
        this.user = user;
        this.apiToken = this.user.token;
        this.user$.next(this.user);

        localStorage.removeItem('authLoggedOut');
        localStorage.setItem('authLastActivity', String(Date.now()));
        localStorage.setItem('authToken', this.user.token);
        sessionStorage.setItem('sessionUser', JSON.stringify(this.user));
        // we do not want to keep doing this. this is continuing the circular dependency issue
        // by having report service and auth service push data to each other.
        // we also don't want [0] so there is a filter, but should be refactored to empty array.
        // TODO: LIQ-1303 to refactor this permission
        this.reportService.validBranches = this.user.permissions.branchLibrary.filter(Boolean);

        this.transloco.setActiveLang(this.user.language || 'en');

        this.favorites.loadUserFavorites(this.user.id);
        // Re-fetch feature flags now that we have a valid auth token.
        // The initial APP_INITIALIZER fetch may have returned incomplete
        // results because the user wasn't authenticated yet.
        this.featureFlags.loadFeatureFlags();
        this.menuService.refreshMenu();
        this.setUserMenuVisibility();

        // Track user login in PostHog
        this.posthogService.trackUserLogin(
            user,
            this.reportService.getLibraryName(),
            this.reportService.getBranchName()
        );

        const url = this.redirectUrl ? this.redirectUrl : '/pages/landingPage';
        this.redirectUrl = '';

        this.router.navigateByUrl(url);
        this.toastrService.info('pages.login.toasts.loginSuccess');
        this.onUserLogin.emit();
    }

    public userLog(message: string): void {
        if (!this.user || !this.user.userName || !message || !this.http) {
            if (message.indexOf('Library Health') === -1) return;
        }
        // Run in the root EnvironmentInjector so the functional HTTP
        // interceptors' inject() calls (tokenInterceptor uses AuthService,
        // ToastrService, TranslocoService) resolve against a live injector
        // even when userLog is invoked from a component's ngOnDestroy —
        // where the ambient injection context is being torn down.
        runInInjectionContext(this.rootInjector, () => {
            this.http
                .post(`${this.API_PREFIX}/login/userLog`, {
                    user: this.user?.userName ? this.user.userName : 'anonymous',
                    message: message,
                    libraryid: this.reportService.currentLibrary,
                    branchid: this.reportService.currentBranch
                })
                .subscribe({
                    error: (e) => console.error(e)
                });
        });
    }
}
